import * as React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { IndianRupee, Check, AlertCircle, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { format } from "date-fns";

interface FeeFormData {
  name?: string;
  paid: string;
  course: string;
  date: string;
  payMode: string;
  bCode: string;
  payable?: string;
}

interface FeeData {
  id?: string;
  Token?: string;
  data: FeeFormData;
}
interface FeeProps {
  initialData: {
    fee?: Array<{
      _id?: string;
      name?: string;
      course?: string;
      bCode?: string;
      student?: string;
      date?: string;
      paid?: string;
      payMode?: string;
    }>;
    TotalFee?: string;
    PaidFee?: string;
  };
  isUpdate: boolean;
  onSubmit?: (data: FeeData) => Promise<void>;
  setIsOpen: (isOpen: boolean) => void;
}

const FeeForm: React.FC<FeeProps> = ({
  initialData,
  isUpdate = false,
  onSubmit,
  setIsOpen,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const { user } = useSelector((state: RootState) => state.user);

  const [formData, setFormData] = React.useState<FeeFormData>({
    name: "",
    paid: "",
    course: "",
    date: "",
    payMode: "",
    bCode: "",
    payable: "",
  });

  React.useEffect(() => {
    if (initialData) {
      if (isUpdate) {
        // Update mode - use the provided data
        setFormData({
          course: initialData.fee?.[0]?.course || "",
          bCode: initialData.fee?.[0]?.bCode || "",
          payable: initialData?.TotalFee || "",
          payMode: initialData.fee[0]?.payMode || "",
          paid: initialData?.fee[0]?.paid || "",
          date: initialData?.fee[0]?.date
            ? format(new Date(initialData?.fee[0]?.date), "yyyy-MM-dd")
            : "", // Reset date for new payment
          name: initialData.fee?.[0]?.name || "",
        });
      } else {
        // Add mode - use initial values
        setFormData({
          name: initialData.fee?.[0]?.name || "",
          paid: "",
          course: initialData.fee?.[0]?.course || "",
          date: "",
          payMode: "",
          bCode: initialData.fee?.[0]?.bCode || "",
          payable: initialData?.TotalFee || "",
        });
      }
    }
  }, [initialData, isUpdate]);

  const paymentModes = [
    { id: "cash", label: "CASH", icon: "💵" },
    { id: "card", label: "CHECK", icon: "📄" },
    { id: "upi", label: "UPI", icon: "📱" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsError(false);

    try {
      const submissionData = {
        id: isUpdate
          ? initialData?.fee?.[0]?._id
          : initialData?.fee?.[0]?.student,
        Token: user?.token,
        data: formData,
      };

      await onSubmit(submissionData);
      toast.success(`Fee ${isUpdate ? "Updated" : "Added"} Successfully`);
      setIsOpen(false);
    } catch (error) {
      setIsError(true);
      toast.error(error || "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  const pendingAmount =
    parseInt(initialData?.TotalFee || "0") -
    parseInt(initialData?.PaidFee || "0");
  const newPendingAmount = pendingAmount - Number(formData.paid);

  return (
    <div className="w-full mx-auto p-4 md:p-6">
      <Card className="border shadow-lg">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-teal-50 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#26BDB0] rounded-lg">
              <IndianRupee className="text-white" size={24} />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold">
                <h1 className="text-black">
                  {isUpdate ? "Update" : "Add"} Fees
                </h1>
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Enter payment details below
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Course Details Section */}
            <div className="p-4 rounded-lg">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Course Information
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.course}
                    disabled
                    className="w-full p-2 border rounded-lg opacity-75"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Batch Code <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.bCode}
                    disabled
                    className="w-full p-2 border rounded-lg opacity-75"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Total Fees <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={formData.payable}
                      onChange={({ target }) =>
                        setFormData((prev) => ({
                          ...prev,
                          payable: target.value,
                        }))
                      }
                      disabled={!isUpdate}
                      className="w-full p-2 pl-8 border rounded-lg bg-background opacity-75"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground">
                      ₹
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details Section */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Payment Details
              </h3>

              {/* Payment Mode */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Payment Mode <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {paymentModes.map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, payMode: mode.id }))
                      }
                      className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-all ${formData.payMode === mode.id
                        ? "border-[#26BDB0] bg-teal-50 text-[#26BDB0]"
                          : "hover:border-gray-300"
                        }`}
                    >
                      <span className="text-2xl">{mode.icon}</span>
                      <span className="text-sm font-medium">{mode.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount and Date */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Fees Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={formData.paid}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          paid: e.target.value,
                        }))
                      }
                      className="w-full text-black p-2 pl-8 border rounded-lg focus:ring-2 focus:ring-[#26BDB0] focus:border-transparent outline-none"
                    />
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      ₹
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1">
                    Paid Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                      className="w-full p-2 text-black border rounded-lg focus:ring-2 focus:ring-[#26BDB0] focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#26BDB0] text-white py-3 rounded-lg hover:bg-[#26BDB0] transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Check size={20} />
                )}
                {isUpdate ? "Update" : "Submit"} Payment
              </button>

              {isError && (
                <p className="text-red-500 text-sm text-center mt-2">
                  Something went wrong! Please try again.
                </p>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="mt-6 border-none shadow-lg bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <AlertCircle className="text-blue-500" size={24} />
            </div>
            <div>
              <h3 className="font-medium text-blue-800">Payment Summary</h3>
              <p className="text-sm text-blue-600 mt-1">
                Pending Amount: ₹{pendingAmount} <br />
                After this payment, pending amount will be ₹{newPendingAmount}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeeForm;
